import dayjs from "dayjs";
import { Dispatch, SetStateAction, createContext, useContext, useMemo, useState } from "react";
import { getChainId, getPlaceId } from "../lib";

interface FuelDataItem {
  id: string;
  name: string;
  totalValue: number;
  totalFillins: number;
}

// Tipos para endpoints consolidados
interface TimeMetricsData {
  averageTime: number | null;
  duration: number | null;
  totalFillins: number;
}

interface PointsSummaryData {
  perDay: Array<{
    id: string;
    date: string;
    aditionPoints: number;
    generatedPoints: number;
    redeemedPoints: number;
  }>;
  topCustomers: Array<{
    id: string;
    totalPoints: number;
  }>;
}

interface FillingBehaviorData {
  perDay: Array<{
    id: string;
    filling_limit: number;
    filling_completely: number;
    total: number;
  }>;
  byFuel: Array<{
    id: string;
    fuel_name: string;
    filling_limit: number;
    filling_completely: number;
    total: number;
  }>;
}

interface DashboardContextValue {
  from: string;
  to: string;
  chain: string;
  origin: string;
  fillinsSummary?: object | null;
  stationIds?: Array<string | null>;
  fuelData: FuelDataItem[] | null;
  fuelDataLoading: boolean;
  // Endpoints consolidados
  timeMetricsData: TimeMetricsData | null;
  timeMetricsLoading: boolean;
  pointsSummaryData: PointsSummaryData | null;
  pointsSummaryLoading: boolean;
  fillingBehaviorData: FillingBehaviorData | null;
  fillingBehaviorLoading: boolean;
  // Setters
  setFrom: Dispatch<SetStateAction<string>>;
  setTo: Dispatch<SetStateAction<string>>;
  setChain: Dispatch<SetStateAction<string>>;
  setOrigin: Dispatch<SetStateAction<string>>;
  setFillinsSummary: Dispatch<SetStateAction<object>>;
  setStationIds: Dispatch<SetStateAction<Array<string | null> | undefined>>;
  setFuelData: Dispatch<SetStateAction<FuelDataItem[] | null>>;
  setFuelDataLoading: Dispatch<SetStateAction<boolean>>;
  setTimeMetricsData: Dispatch<SetStateAction<TimeMetricsData | null>>;
  setTimeMetricsLoading: Dispatch<SetStateAction<boolean>>;
  setPointsSummaryData: Dispatch<SetStateAction<PointsSummaryData | null>>;
  setPointsSummaryLoading: Dispatch<SetStateAction<boolean>>;
  setFillingBehaviorData: Dispatch<SetStateAction<FillingBehaviorData | null>>;
  setFillingBehaviorLoading: Dispatch<SetStateAction<boolean>>;
};

const DashboardContext = createContext({} as DashboardContextValue);

export const DashboardProvider = ({ children }) => {
  const initalStation = getPlaceId() ? [getPlaceId()] : undefined

  const [from, setFrom] = useState(dayjs().startOf('day').subtract(1, 'month').toISOString());
  const [to, setTo] = useState(dayjs().endOf('day').toISOString());
  const [chain, setChain] = useState(getChainId());
  const [origin, setOrigin] = useState("");
  const [stationIds, setStationIds] = useState(initalStation);
  const [fillinsSummary, setFillinsSummary] = useState({});
  const [fuelData, setFuelData] = useState<FuelDataItem[] | null>(null);
  const [fuelDataLoading, setFuelDataLoading] = useState(false);

  // Estados para endpoints consolidados
  const [timeMetricsData, setTimeMetricsData] = useState<TimeMetricsData | null>(null);
  const [timeMetricsLoading, setTimeMetricsLoading] = useState(false);
  const [pointsSummaryData, setPointsSummaryData] = useState<PointsSummaryData | null>(null);
  const [pointsSummaryLoading, setPointsSummaryLoading] = useState(false);
  const [fillingBehaviorData, setFillingBehaviorData] = useState<FillingBehaviorData | null>(null);
  const [fillingBehaviorLoading, setFillingBehaviorLoading] = useState(false);

  const value = useMemo(() => ({
    from,
    to,
    chain,
    origin,
    fillinsSummary,
    stationIds,
    fuelData,
    fuelDataLoading,
    timeMetricsData,
    timeMetricsLoading,
    pointsSummaryData,
    pointsSummaryLoading,
    fillingBehaviorData,
    fillingBehaviorLoading,
    setFrom,
    setTo,
    setChain,
    setOrigin,
    setFillinsSummary,
    setStationIds,
    setFuelData,
    setFuelDataLoading,
    setTimeMetricsData,
    setTimeMetricsLoading,
    setPointsSummaryData,
    setPointsSummaryLoading,
    setFillingBehaviorData,
    setFillingBehaviorLoading,
  }), [
    from, to, chain, origin, fillinsSummary, stationIds,
    fuelData, fuelDataLoading, timeMetricsData, timeMetricsLoading,
    pointsSummaryData, pointsSummaryLoading, fillingBehaviorData, fillingBehaviorLoading,
  ]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

const useDashboardContext = () => useContext(DashboardContext);

export default useDashboardContext;